/**
 * CashRegisterEditionController — page `/caisse-edition.html`
 *
 * EPIC-043 / 43.4 — Ouverture d'une session de caisse (§7.J) :
 *   - charge les activités actives pour peupler le `<select>` (single si
 *     1 activité, multi avec « Toutes activités » sinon) ;
 *   - initialise `sessionDate` à aujourd'hui (jamais une date future) ;
 *   - génère un `Idempotency-Key` (UUID v4) au `connect()` pour neutraliser
 *     les doubles soumissions ;
 *   - POST /v1/cash-register/sessions et redirige sur la fiche au 201 ;
 *   - 422 → erreurs par champ via `nm-field__error` ;
 *   - 423 → bannière critique (exercice clôturé) ;
 *   - autres codes → toast d'erreur générique via BaseController.
 *
 * Convention `frontend/CLAUDE.md` : aucune construction HTML inline en JS —
 * seuls les `<option>` du sélecteur activité sont clonés depuis le template
 * `#tpl-activity-option`.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

export default class CashRegisterEditionController extends BaseController {
  static targets = [
    "form",
    "sessionDateInput",
    "activitySelect",
    "openingBalanceInput",
    "carryoverBtn",
    "submitBtn",
    "errorBanner",
  ];

  connect() {
    this._idempotencyKey = this._makeIdempotencyKey();
    this._previousClosing = null;
    this._setSessionDateToToday();
    this._loadActivities();
    this._loadPreviousClosing();
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  _setSessionDateToToday() {
    if (!this.hasSessionDateInputTarget) return;
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    this.sessionDateInputTarget.value = `${yyyy}-${mm}-${dd}`;
    this.sessionDateInputTarget.max = `${yyyy}-${mm}-${dd}`;
  }

  async _loadActivities() {
    if (!this.hasActivitySelectTarget) return;
    try {
      const response = await api.listActivities();
      const list = Array.isArray(response?.activities)
        ? response.activities
        : Array.isArray(response?.data) ? response.data : [];
      const activities = list.filter((a) => (a.status ?? "active") === "active");
      this._populateActivitySelect(activities);
    } catch (error) {
      // Non bloquant : l'utilisateur peut toujours ouvrir sans activité.
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger la liste des activités.";
      this.showError(msg);
    }
  }

  /**
   * Récupère le comptage final (`closingBalance`) de la dernière session
   * clôturée pour proposer un report via le bouton « Reprendre de la session
   * précédente ». Silencieux en cas d'échec : le bouton reste masqué et la
   * saisie reste manuelle (le report n'est qu'un confort, pas un prérequis).
   */
  async _loadPreviousClosing() {
    if (!this.hasCarryoverBtnTarget) return;
    try {
      const response = await api.cashRegister.listSessions({ status: "closed", perPage: 1, page: 1 });
      const sessions = Array.isArray(response?.data) ? response.data : [];
      const last = sessions[0];
      if (last && last.closingBalance != null && last.closingBalance !== "") {
        this._previousClosing = String(last.closingBalance);
        this.carryoverBtnTarget.hidden = false;
      }
    } catch (_error) {
      // Non bloquant : pas de report proposé.
    }
  }

  /**
   * Reporte le comptage final de la dernière session clôturée dans le champ
   * « Fond de caisse initial ». Le ME peut ensuite ajuster s'il a retiré des
   * espèces depuis (dépôt en banque, prélèvement…).
   */
  carryOver(event) {
    if (event) event.preventDefault();
    if (this._previousClosing == null || !this.hasOpeningBalanceInputTarget) return;
    this.openingBalanceInputTarget.value = this._previousClosing;
    this.openingBalanceInputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.openingBalanceInputTarget.focus();
  }

  _populateActivitySelect(activities) {
    const select = this.activitySelectTarget;
    // Vider toutes les options sauf une éventuelle première placeholder
    // (laissée intentionnellement par la maquette : « Toutes activités »).
    const keepFirst = select.options.length > 0 && select.options[0].value === "";
    while (select.options.length > (keepFirst ? 1 : 0)) {
      select.remove(keepFirst ? 1 : 0);
    }

    if (activities.length === 0) return;

    // Cas N=1 : on pré-sélectionne directement, mais on garde un select pour
    // un parcours utilisateur uniforme (idem articles-edition.html).
    if (activities.length === 1) {
      const opt = this._cloneOption(activities[0]);
      select.appendChild(opt);
      select.value = activities[0].id;
      return;
    }

    // Cas N>1 : on liste toutes les activités, « Toutes activités » reste
    // disponible en valeur vide (multi-activité).
    for (const activity of activities) {
      select.appendChild(this._cloneOption(activity));
    }
  }

  _cloneOption(activity) {
    const fragment = this.cloneTemplate("tpl-activity-option");
    if (!fragment) {
      // Fallback minimal si le template a été retiré : on construit un
      // <option> nu (aucun HTML structuré, juste un élément primitif).
      const opt = document.createElement("option");
      opt.value = activity.id;
      opt.textContent = activity.description || activity.label || activity.id;
      return opt;
    }
    const opt = fragment.querySelector("option");
    this.fillSlots(fragment, { text: activity.description || activity.label || activity.id });
    if (opt) opt.value = activity.id;
    return opt ?? fragment;
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    this._hideAllErrors();

    const payload = this._collectPayload();
    const clientErrors = this._validatePayloadClientSide(payload);
    if (Object.keys(clientErrors).length > 0) {
      this._showFieldErrors(clientErrors);
      return;
    }

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;

    try {
      const response = await api.cashRegister.createSession(payload, this._idempotencyKey);
      const sessionId = response?.id ?? response?.data?.id ?? null;
      if (!sessionId) {
        // Le backend devrait toujours renvoyer l'id, mais on dégrade vers
        // la liste plutôt que de bloquer l'utilisateur si jamais.
        window.location.href = "/caisse.html";
        return;
      }
      window.location.href = `/caisse-session.html?id=${encodeURIComponent(sessionId)}`;
    } catch (error) {
      this._handleSubmitError(error);
    } finally {
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;
    }
  }

  _collectPayload() {
    const form = this.hasFormTarget ? this.formTarget : this.element;
    const sessionDate = this.hasSessionDateInputTarget ? this.sessionDateInputTarget.value : "";
    const openingBalanceRaw = this.hasOpeningBalanceInputTarget
      ? this.openingBalanceInputTarget.value
      : "";
    const labelInput = form.querySelector("#label");
    const notesInput = form.querySelector("#notes");
    const activityId = this.hasActivitySelectTarget ? (this.activitySelectTarget.value || null) : null;

    return {
      sessionDate,
      openingBalance: openingBalanceRaw === "" ? null : Number(openingBalanceRaw),
      label: labelInput ? (labelInput.value.trim() || null) : null,
      activityId,
      notes: notesInput ? (notesInput.value.trim() || null) : null,
    };
  }

  _validatePayloadClientSide(payload) {
    const errors = {};
    if (!payload.sessionDate) {
      errors.sessionDate = "Date obligatoire.";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const picked = new Date(payload.sessionDate + "T00:00:00");
      if (picked > today) errors.sessionDate = "La date ne peut pas être dans le futur.";
    }
    if (payload.openingBalance === null || !Number.isFinite(payload.openingBalance)) {
      errors.openingBalance = "Fond de caisse obligatoire.";
    } else if (payload.openingBalance < 0) {
      errors.openingBalance = "Le fond de caisse doit être ≥ 0.";
    }
    return errors;
  }

  _handleSubmitError(error) {
    if (!(error instanceof APIError)) {
      this.showError("Erreur réseau. Réessayez.");
      return;
    }

    if (error.status === 422 && error.data?.error?.details?.fields) {
      this._showFieldErrors(error.data.error.details.fields);
      return;
    }

    if (error.status === 423) {
      this._showErrorBanner(
        "Exercice clôturé",
        "Vous ne pouvez pas ouvrir de session à cette date — l'exercice fiscal correspondant a été clôturé.",
      );
      return;
    }

    this.showError(error.message || "Impossible d'ouvrir la session.");
  }

  // ---------------------------------------------------------------------------
  // Error display
  // ---------------------------------------------------------------------------

  _showFieldErrors(fields) {
    for (const [field, message] of Object.entries(fields)) {
      const el = this.element.querySelector(`[data-cash-register-edition-target="fieldError-${field}"]`);
      if (!el) continue;
      el.textContent = typeof message === "string" ? message : "Valeur invalide.";
      el.hidden = false;
    }
  }

  _hideAllErrors() {
    for (const el of this.element.querySelectorAll('[data-cash-register-edition-target^="fieldError-"]')) {
      el.hidden = true;
      el.textContent = "";
    }
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = true;
  }

  _showErrorBanner(title, message) {
    if (!this.hasErrorBannerTarget) {
      this.showError(`${title} — ${message}`);
      return;
    }
    const titleEl = this.errorBannerTarget.querySelector('[data-slot="error-title"]');
    const msgEl = this.errorBannerTarget.querySelector('[data-slot="error-message"]');
    if (titleEl) titleEl.textContent = title;
    if (msgEl) msgEl.textContent = " " + message;
    this.errorBannerTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _makeIdempotencyKey() {
    return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : "fb-" + Date.now() + "-" + Math.random().toString(16).slice(2);
  }
}
